import { graphql } from 'react-relay';
import { useMemo } from 'react';
import { usePageContentId } from '@confluence/page-context';
import {
	useExternalLabelsFragment,
	useExternalLabelsFragmentVariables,
	type ExternalLabel,
} from '@confluence/labels/entry-points/useExternalLabels.ts';

import { normalizeContentLabelsData } from '../normalizers/normalizeContentLabelsData.ts';

import type {
	useContentLabelsDetailsPanelContentLabelDeleteMutation$data,
	useContentLabelsDetailsPanelContentLabelDeleteMutation$variables,
} from './__generated__/useContentLabelsDetailsPanelContentLabelDeleteMutation.graphql.ts';
import type {
	useContentLabelsDetailsPanelContentLabelsQuery$data,
	useContentLabelsDetailsPanelContentLabelsQuery$variables,
} from './__generated__/useContentLabelsDetailsPanelContentLabelsQuery.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutationFunctionInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
const useContentLabelsDetailsPanelContentLabelsQuery = graphql`
	query useContentLabelsDetailsPanelContentLabelsQuery(
		$contentId: ID!
		$includeExternalLabels: Boolean = false
		$externalLabelsCloudId: ID!
		$externalLabelsAri: ID!
	) {
		singleContent(id: $contentId) {
			id
			labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
				count
				nodes {
					id
					label
					name
					prefix
				}
			}
		}
		content(id: $contentId, status: ["archived", "current", "draft"]) {
			nodes {
				id
				type
				operations {
					operation
					targetType
				}
			}
		}
		...useExternalLabels_query
			@arguments(cloudId: $externalLabelsCloudId, id: $externalLabelsAri)
			@alias(as: "externalLabelsFragment")
			@include(if: $includeExternalLabels)
	}
`;
const useContentLabelsDetailsPanelContentLabelDeleteMutation = graphql`
	mutation useContentLabelsDetailsPanelContentLabelDeleteMutation(
		$contentId: ID!
		$label: String!
	) {
		deleteLabel(input: { contentId: $contentId, label: $label }) {
			contentId
		}
	}
`;
export const useContentLabels = (): {
	data: {
		labels: (
			| {
					readonly id: string | null | undefined;
					readonly label: string | null | undefined;
					readonly name: string | null | undefined;
					readonly prefix: string | null | undefined;
			  }
			| null
			| undefined
		)[];
		hasEditPermissions: boolean;
	};
	// Managed (external) labels ride on the same query via the aliased
	// `useExternalLabels_query` fragment, so they refetch with `refetch` below.
	managedLabels: readonly ExternalLabel[];
	handleDeleteLabelClick: MutationFunctionInterop<
		useContentLabelsDetailsPanelContentLabelDeleteMutation$data,
		useContentLabelsDetailsPanelContentLabelDeleteMutation$variables
	>;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	refetch: RefetchSupportedType<
		useContentLabelsDetailsPanelContentLabelsQuery$data,
		useContentLabelsDetailsPanelContentLabelsQuery$variables
	>;
} => {
	const [contentId = ''] = usePageContentId();
	const externalLabelsVariables = useExternalLabelsFragmentVariables(contentId);
	const { data, loading, error, refetch } = useQueryInterop<
		useContentLabelsDetailsPanelContentLabelsQuery$data,
		useContentLabelsDetailsPanelContentLabelsQuery$variables
	>(useContentLabelsDetailsPanelContentLabelsQuery, {
		variables: {
			contentId,
			includeExternalLabels: externalLabelsVariables.includeExternalLabels,
			externalLabelsCloudId: externalLabelsVariables.cloudId,
			externalLabelsAri: externalLabelsVariables.id,
		},
		skip: !contentId,
	});
	const [handleDeleteLabelClick] = useMutationInterop<
		useContentLabelsDetailsPanelContentLabelDeleteMutation$data,
		useContentLabelsDetailsPanelContentLabelDeleteMutation$variables
	>(useContentLabelsDetailsPanelContentLabelDeleteMutation);

	const normalizedData = useMemo(() => normalizeContentLabelsData(data), [data]);
	const managedLabels = useExternalLabelsFragment(data?.externalLabelsFragment);

	return {
		data: normalizedData,
		managedLabels,
		handleDeleteLabelClick,
		loading,
		error,
		refetch,
	};
};
